import * as _ from 'lodash';
import * as moment from 'moment';
import { Component } from 'react';
import { Dispatch, connect } from 'react-redux';
import { IReportState, IApplicationState, AppActionCreators, ReportActionCreators, IKidneyAvailOnTimeReportResponse, IKidneyAvailOnTimeReports } from '@nkr/store/index';
import { Well } from 'react-bootstrap';
import { downloadFile } from '@nkr/services/download-file';
import Filter from './filter';
import KidneyAvailOnTimeReportGrid from './kidney-available-on-time-grid';

interface IOwnProps { }

interface IStateProps {
    reports: IReportState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getKidneyAvailableOnTime: (startDate: string, endDate: string) => Promise<IKidneyAvailOnTimeReportResponse>,
    getkidneyAvailableOnTimeExport: (startDate: string, endDate: string) => Promise<any>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    startDate: string,
    endDate: string,
    centerId: number,
    report: IKidneyAvailOnTimeReports,
    showSummary: boolean,
    showDefinitions: boolean,
    showDateWarning: boolean
}

class KidneyAvailOnTimeReport extends Component<TProps, IState> {
    readonly state: IState = {
        startDate: '',
        endDate: '',
        centerId: 0,
        report: null,
        showSummary: false,
        showDefinitions: false,
        showDateWarning: false
    }

    componentDidMount = () => {
        let startDate = '';
        let endDate = '';

        if (startDate == '' || endDate == '') {
            startDate = moment().startOf('month').subtract(1, 'years').format('YYYY-MM-DD');
            endDate = moment().endOf('month').format('YYYY-MM-DD');
        } else {
            startDate = this.state.startDate;
            endDate = this.state.endDate;
        }

        this.getData(startDate, endDate);
    }

    getData = (startDate: string, endDate: string) => {
        this.props.showLoading('Getting report data, one moment please...').then(() => {
            this.props.getKidneyAvailableOnTime(startDate, endDate)
                .then(response => {
                    this.setState({
                        startDate,
                        endDate,
                        report: response.data.results
                    });
                })
                .catch((ex) => {
                    alert('An error occured while loading the report data');
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    validate = () => {
        if (this.state.startDate == '' || this.state.endDate == '') {
            return false;
        }

        const yearRegex = /^[1-9]\d{3}-\d{2}-\d{2}$/;

        if (!yearRegex.test(this.state.startDate) || !yearRegex.test(this.state.endDate)) {
            return false;
        }

        let startDate = moment(this.state.startDate, 'YYYY-MM-DD', true);
        let endDate = moment(this.state.endDate, 'YYYY-MM-DD', true);

        if (startDate.isValid() == false || endDate.isValid() == false) {
            return false;
        }

        return true;
    }

    handleSearch = () => {
        let isValid = this.validate();

        this.setState({
            showDateWarning: isValid == false
        }, () => {
            if (isValid) {
                this.getData(this.state.startDate, this.state.endDate);
            }
        });
    }

    handleToggleSummary = () => {
        this.setState({ showSummary: !this.state.showSummary });
    };

    handleToggleDefinitions = () => {
        this.setState({ showDefinitions: !this.state.showDefinitions });
    }

    handleSearchChange = (name: string, value: any) => {
        if (name == 'startDate') {
            this.setState({
                startDate: value
            });
        }

        if (name == 'endDate') {
            this.setState({
                endDate: value
            });
        }
    }

    handleExport = () => {
        this.props.getkidneyAvailableOnTimeExport(this.state.startDate, this.state.endDate).then((response: any) => {
            downloadFile(response.data, `kidney-available-on-time-results.csv`, 'text/csv');
        });
    }

    render = () => {
        return (
            <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div style={{ fontSize: 28, fontWeight: 500 }}>Kidney Available On Time</div>
                </div>

                <Filter
                    startDate={this.state.startDate}
                    endDate={this.state.endDate}
                    showDefinitions={this.state.showDefinitions}
                    showSummary={this.state.showSummary}
                    showDateWarning={this.state.showDateWarning}
                    onSearch={this.handleSearch}
                    onSearchChange={this.handleSearchChange}
                    onToggleDefinitions={this.handleToggleDefinitions}
                    onToggleSummary={this.handleToggleSummary}
                    onExport={this.handleExport}
                />

                {this.state.showDefinitions &&
                    <Well bsSize='sm'>
                        <div>Event Lateness Definitions</div>
                        <ol>
                            <li>Less than 16 minutes is not late</li>
                            <li>Less than 61 minutes is late</li>
                            <li>All else is very late</li>
                        </ol>
                        <div>Center Lateness Definitions for Summary View</div>
                        <ol style={{ marginBottom: 0 }}>
                            <li>Late % + Very Late %&nbsp;&nbsp;25% - 50% highlights yellow</li>
                            <li>Late % + Very Late %&nbsp;&nbsp;50% highlights red</li>
                            <li>All else isn't highlighted</li>
                        </ol>
                    </Well>
                }

                <KidneyAvailOnTimeReportGrid
                    show={this.state.showSummary}
                    report={this.state.report}
                />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getKidneyAvailableOnTime: (startDate, endDate) => {
            return dispatch(ReportActionCreators.getKidneyAvailableOnTime(startDate, endDate));
        },
        getkidneyAvailableOnTimeExport: (startDate, endDate) => {
            return dispatch(ReportActionCreators.getkidneyAvailableOnTimeExport(startDate, endDate));
        },
    }
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(KidneyAvailOnTimeReport);